// SalesEmailHistory.jsx — Full email send history for the logged-in sales user
// Shows all emails (original, reply, forward, followup) in one paginated table.
// Real-time updates via socket: new sends + status changes arrive without page refresh.

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Table, Tag, Input, Select, DatePicker, Tooltip, Spin, Badge } from "antd";
import {
  faEnvelopeOpen, faReply, faCircleXmark, faStop,
  faClock, faTruck, faChevronDown, faChevronUp, faFilter,
  faRefresh, faDesktop, faMobile, faTablet, faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SERVER_URL } from "../../../databucket";
import socket from "./socket";

dayjs.extend(relativeTime);

const { RangePicker } = DatePicker;
const { Option }      = Select;

// ── Status config ──────────────────────────────────────────────────────────────
const STATUS_CFG = {
  pending:      { color: "#6366f1", bg: "#eef2ff", label: "Pending",       icon: faClock,        border: "#a5b4fc" },
  delivered:    { color: "#3b82f6", bg: "#eff6ff", label: "Delivered",     icon: faTruck,        border: "#93c5fd" },
  opened:       { color: "#10b981", bg: "#ecfdf5", label: "Opened",        icon: faEnvelopeOpen, border: "#6ee7b7" },
  clicked:      { color: "#0ea5e9", bg: "#f0f9ff", label: "Clicked",       icon: faEnvelopeOpen, border: "#7dd3fc" },
  replied:      { color: "#8b5cf6", bg: "#f5f3ff", label: "Replied",       icon: faReply,        border: "#c4b5fd" },
  bounced:      { color: "#ef4444", bg: "#fef2f2", label: "Hard Bounce",   icon: faCircleXmark,  border: "#fca5a5" },
  soft_bounced: { color: "#f97316", bg: "#fff7ed", label: "Soft Bounce",   icon: faCircleXmark,  border: "#fdba74" },
  complained:   { color: "#dc2626", bg: "#fef2f2", label: "Spam Complaint",icon: faStop,         border: "#f87171" },
  rejected:     { color: "#7c3aed", bg: "#f5f3ff", label: "Rejected",      icon: faCircleXmark,  border: "#a78bfa" },
};

const EMAIL_TYPE_CFG = {
  original:  { color: "#3b82f6", label: "Original"  },
  reply:     { color: "#8b5cf6", label: "Reply"     },
  forward:   { color: "#f59e0b", label: "Forward"   },
  followup:  { color: "#10b981", label: "Follow-up" },
};

const DEVICE_ICON = { Desktop: faDesktop, Mobile: faMobile, Tablet: faTablet };

function StatusBadge({ status, bounceReason }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  const badge = (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 12,
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`,
      fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      <FontAwesomeIcon icon={cfg.icon} style={{ fontSize: 10 }} />
      {cfg.label}
    </span>
  );
  if (bounceReason && (status === "bounced" || status === "soft_bounced" || status === "complained" || status === "rejected")) {
    return <Tooltip title={bounceReason} placement="top">{badge}</Tooltip>;
  }
  return badge;
}

function TypeTag({ emailType }) {
  const cfg = EMAIL_TYPE_CFG[emailType] || EMAIL_TYPE_CFG.original;
  return (
    <Tag color={cfg.color} style={{ fontSize: 10, fontWeight: 600, borderRadius: 8 }}>
      {cfg.label}
    </Tag>
  );
}

function StatCard({ label, value, color, sub }) {
  return (
    <div style={{
      flex: "1 1 120px", minWidth: 100, maxWidth: 180,
      background: "#fff", border: "1px solid #e2e8f0",
      borderRadius: 10, padding: "14px 16px",
      boxShadow: "0 1px 4px #0001",
    }}>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value ?? 0}</div>
      <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{label}</div>
      {sub != null && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>{sub} opens</div>}
    </div>
  );
}

function OpenEventRow({ open }) {
  const deviceIcon = DEVICE_ICON[open.device?.type] || faGlobe;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "6px 0", borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#475569",
    }}>
      <FontAwesomeIcon icon={deviceIcon} style={{ color: "#94a3b8", fontSize: 13, width: 16 }} />
      <span style={{ fontWeight: 500 }}>{open.device?.browser || "Unknown"}</span>
      <span style={{ color: "#94a3b8" }}>{open.device?.os || ""}</span>
      <span style={{ color: "#94a3b8" }}>•</span>
      <span>{open.city || open.country || "—"}{open.country && open.city ? `, ${open.country}` : ""}</span>
      <span style={{ color: "#94a3b8" }}>•</span>
      <span style={{ color: "#94a3b8" }}>{dayjs(open.at).fromNow()}</span>
      {open.ip && <span style={{ color: "#cbd5e1", fontSize: 10, marginLeft: "auto" }}>{open.ip}</span>}
    </div>
  );
}

function ExpandedRow({ record }) {
  return (
    <div style={{ padding: "10px 20px 10px 48px", background: "#f8fafc" }}>
      {/* Thread link */}
      {record.parentMessageId && (
        <div style={{ marginBottom: 10, fontSize: 12, color: "#64748b" }}>
          <b style={{ color: "#6366f1" }}>↩ Thread:</b> Linked to{" "}
          <span style={{ fontFamily: "monospace", fontSize: 11 }}>
            {record.parentMessageId.split("@")[0]}…
          </span>
        </div>
      )}

      {/* Bounce / Complaint details */}
      {(record.status === "bounced" || record.status === "soft_bounced" || record.status === "complained" || record.status === "rejected") && (
        <div style={{
          background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8,
          padding: "10px 14px", marginBottom: 10, fontSize: 12, color: "#b91c1c",
        }}>
          <b>
            {record.status === "complained" ? "Spam Complaint" :
             record.status === "soft_bounced" ? "Soft Bounce" :
             record.status === "rejected" ? "Rejected by SES" : "Hard Bounce"}
          </b>
          {record.bounceReason && <span style={{ color: "#991b1b", marginLeft: 8 }}>{record.bounceReason}</span>}
          {record.bouncedAt && (
            <span style={{ color: "#b91c1c", marginLeft: 8 }}>at {dayjs(record.bouncedAt).format("MMM D, h:mm A")}</span>
          )}
        </div>
      )}

      {/* Reply info */}
      {record.status === "replied" && (
        <div style={{
          background: "#f5f3ff", border: "1px solid #c4b5fd", borderRadius: 8,
          padding: "10px 14px", marginBottom: 10, fontSize: 12, color: "#5b21b6",
        }}>
          <b>Reply received</b> from{" "}
          <span style={{ fontWeight: 600 }}>{record.replyFrom || "—"}</span>
          {record.replySubject && (
            <span style={{ color: "#6d28d9", marginLeft: 8 }}>· "{record.replySubject}"</span>
          )}
          {record.repliedAt && (
            <span style={{ color: "#7c3aed", marginLeft: 8 }}>{dayjs(record.repliedAt).fromNow()}</span>
          )}
        </div>
      )}

      {/* Open events */}
      {record.opens?.length > 0 ? (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
            Open Events ({record.opens.length})
          </div>
          {record.opens.map((o, i) => <OpenEventRow key={i} open={o} />)}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: "#94a3b8" }}>No open events recorded.</div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function SalesEmailHistory() {
  const loginUser   = useSelector(s => s?.loginUserInfo?.loginUserDetails);
  const sharedemails = useSelector(s => s?.sharedEmail?.sharedemails || []);

  // Build userEmails from sales-configured accounts only.
  // Intentionally excludes the personal login email (loginUser.email) and personal
  // Outlook emailSettings — this page is for SES sends from sales email accounts.
  const userEmails = useMemo(() => {
    // 1. salesEmailSettings — the primary sales-configured email addresses
    const fromSales = loginUser?.salesEmailSettings
      ?.filter(i => i.status === true || i.status === "true")
      ?.map(i => i.email)
      ?.filter(Boolean) || [];

    // 2. sharedemailSettings — shared inboxes the user has access to
    const emailMap = sharedemails.reduce((acc, i) => { acc[i._id] = i.email; return acc; }, {});
    const fromShared = (loginUser?.sharedemailSettings || [])
      .filter(i => i.status === true || i.status === "true")
      .map(i => emailMap[i.email])
      .filter(Boolean);

    return Array.from(new Set([...fromSales, ...fromShared]));
  }, [loginUser, sharedemails]);

  // ── State ──────────────────────────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [stats,   setStats]   = useState({});
  const [loading, setLoading] = useState(false);
  const [page,    setPage]    = useState(1);
  const [limit]               = useState(20);

  // Filters
  const [search,   setSearch]   = useState("");
  const [statuses, setStatuses] = useState([]);
  const [types,    setTypes]    = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);

  const searchTimer = useRef(null);
  const isMounted   = useRef(true);

  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);

  const userId = loginUser?._id || loginUser?.id;

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchHistory = useCallback(async (opts = {}) => {
    if (!userId) return; // Wait until user is loaded
    setLoading(true);
    try {
      const params = {
        page:  opts.page ?? page,
        limit,
        ...(userEmails.length && { emails: userEmails.join(",") }),
        ...(userId            && { userId }),
        ...(search            && { search }),
        ...(statuses.length   && { status: statuses.join(",") }),
        ...(types.length      && { type:   types.join(",")   }),
        ...(dateRange[0]      && { dateFrom: dateRange[0].toISOString() }),
        ...(dateRange[1]      && { dateTo:   dateRange[1].toISOString() }),
      };
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${SERVER_URL}/api/ses/email-history`, {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!isMounted.current) return;
      setRows(data.rows  || []);
      setTotal(data.total || 0);
      setStats(data.stats || {});
    } catch (err) {
      console.error("Email history fetch error:", err.message);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [userEmails, userId, page, limit, search, statuses, types, dateRange]);

  // Initial + filter-change fetch
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Debounced search
  const handleSearchChange = (val) => {
    setSearch(val);
    setPage(1);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchHistory({ page: 1 }), 400);
  };

  // ── Real-time socket updates ───────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    const handleSesEvent = (evt) => {
      // Update status of an existing row in-place
      setRows(prev => prev.map(r => {
        if (r.messageId !== evt.messageId && r.trackId !== evt.trackId) return r;
        const currentPriority = STATUS_CFG[r.status] ? Object.keys(STATUS_CFG).indexOf(r.status) : 0;
        const newPriority     = STATUS_CFG[evt.status] ? Object.keys(STATUS_CFG).indexOf(evt.status) : 0;
        if (newPriority <= currentPriority && r.status !== "pending") return r;
        return {
          ...r,
          status:     evt.status    || r.status,
          openCount:  evt.openCount ?? r.openCount,
          replyFrom:  evt.from      || r.replyFrom,
          replySubject: evt.subject || r.replySubject,
          repliedAt:  evt.status === "replied" ? new Date().toISOString() : r.repliedAt,
        };
      }));
      // Also refresh stats silently
      setStats(prev => {
        if (!evt.status) return prev;
        return { ...prev, [evt.status]: (prev[evt.status] || 0) + 1 };
      });
    };

    const handleNewEmail = () => {
      // A new email was sent — prepend it by refetching page 1
      if (page === 1) fetchHistory({ page: 1 });
    };

    socket.on("email:sesEvent", handleSesEvent);
    socket.on("email:newSent",  handleNewEmail);
    return () => {
      socket.off("email:sesEvent", handleSesEvent);
      socket.off("email:newSent",  handleNewEmail);
    };
  }, [page, fetchHistory]);

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = [
    {
      title: "#",
      width: 52,
      render: (_, __, idx) => (
        <span style={{ color: "#94a3b8", fontSize: 12 }}>
          {(page - 1) * limit + idx + 1}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "emailType",
      width: 90,
      render: t => <TypeTag emailType={t} />,
    },
    {
      title: "From",
      dataIndex: "fromEmail",
      ellipsis: true,
      width: 180,
      render: v => (
        <Tooltip title={v}>
          <span style={{ fontSize: 12, color: "#374151" }}>{v}</span>
        </Tooltip>
      ),
    },
    {
      title: "To",
      dataIndex: "toEmails",
      ellipsis: true,
      width: 200,
      render: arr => {
        const list = arr || [];
        const display = list[0] || "—";
        return (
          <Tooltip title={list.join(", ")}>
            <span style={{ fontSize: 12, color: "#374151" }}>
              {display}{list.length > 1 && <span style={{ color: "#94a3b8" }}> +{list.length - 1}</span>}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Subject",
      dataIndex: "subject",
      ellipsis: true,
      render: (v, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {row.parentMessageId && (
            <Tooltip title={`Linked to parent: ${row.parentMessageId.split("@")[0]}`}>
              <span style={{ color: "#8b5cf6", fontSize: 11 }}>↩</span>
            </Tooltip>
          )}
          <Tooltip title={v}>
            <span style={{ fontSize: 12, color: "#1e293b", fontWeight: 500 }}>{v || "(No subject)"}</span>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Agency",
      dataIndex: "agencyName",
      ellipsis: true,
      width: 140,
      render: v => <span style={{ fontSize: 12, color: "#374151" }}>{v || "—"}</span>,
    },
    {
      title: "Lead ID",
      dataIndex: "leadDisplayId",
      width: 90,
      render: (v, row) =>
        v ? (
          <a href={`/sales/${row.leadId}`} style={{ fontSize: 11, color: "#6366f1" }} target="_blank" rel="noreferrer">
            #{v}
          </a>
        ) : <span style={{ color: "#cbd5e1", fontSize: 11 }}>—</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 130,
      render: (v, row) => <StatusBadge status={v} bounceReason={row.bounceReason} />,
    },
    {
      title: "Opens",
      dataIndex: "openCount",
      width: 65,
      align: "center",
      render: v => v > 0
        ? <Badge count={v} color="#10b981" style={{ fontSize: 11 }} />
        : <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>,
    },
    {
      title: "Sent",
      dataIndex: "sentAt",
      width: 120,
      render: v => (
        <Tooltip title={dayjs(v).format("MMM D YYYY, h:mm A")}>
          <span style={{ fontSize: 11, color: "#64748b" }}>{dayjs(v).fromNow()}</span>
        </Tooltip>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  const filterActive = search || statuses.length > 0 || types.length > 0 || dateRange[0];

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f8fafc",
      padding: "24px 28px",
      fontFamily: "'Inter', system-ui, sans-serif",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#1e293b" }}>
            Email Send History
          </h2>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
            All emails sent from your accounts — original, reply, forward, and follow-up
          </div>
        </div>
        <button
          onClick={() => fetchHistory()}
          style={{
            display: "flex", alignItems: "center", gap: 7,
            padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0",
            background: "#fff", color: "#475569", fontSize: 13, fontWeight: 500,
            cursor: "pointer", boxShadow: "0 1px 3px #0001",
          }}
        >
          <FontAwesomeIcon icon={faRefresh} spin={loading} style={{ fontSize: 12 }} />
          Refresh
        </button>
      </div>

      {/* Stats bar */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        <StatCard label="Total Sent"     value={stats.total}       color="#1e293b" />
        <StatCard label="Pending"        value={stats.pending}     color="#6366f1" />
        <StatCard label="Delivered"      value={stats.delivered}   color="#3b82f6" />
        <StatCard label="Opened"         value={stats.opened}      color="#10b981" sub={stats.totalOpens} />
        <StatCard label="Replied"        value={stats.replied}     color="#8b5cf6" />
        <StatCard label="Hard Bounce"    value={stats.bounced}     color="#ef4444" />
        <StatCard label="Soft Bounce"    value={stats.softBounced} color="#f97316" />
        <StatCard label="Complained"     value={stats.complained}  color="#dc2626" />
        <StatCard label="Rejected"       value={stats.rejected}    color="#7c3aed" />
      </div>

      {/* Filter bar */}
      <div style={{
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
        background: "#fff", border: "1px solid #e2e8f0",
        borderRadius: 10, padding: "12px 16px", marginBottom: 16,
        boxShadow: "0 1px 3px #0001",
      }}>
        <FontAwesomeIcon icon={faFilter} style={{ color: filterActive ? "#6366f1" : "#94a3b8", fontSize: 13 }} />

        <Input.Search
          placeholder="Search subject, recipient, agency…"
          value={search}
          onChange={e => handleSearchChange(e.target.value)}
          onSearch={() => { setPage(1); fetchHistory({ page: 1 }); }}
          allowClear
          style={{ width: 260 }}
          size="small"
        />

        <Select
          mode="multiple"
          placeholder="Status"
          value={statuses}
          onChange={v => { setStatuses(v); setPage(1); }}
          style={{ minWidth: 200 }}
          size="small"
          allowClear
          maxTagCount={2}
        >
          {Object.entries(STATUS_CFG).map(([k, v]) => (
            <Option key={k} value={k}>
              <span style={{ color: v.color }}>{v.label}</span>
            </Option>
          ))}
        </Select>

        <Select
          mode="multiple"
          placeholder="Email type"
          value={types}
          onChange={v => { setTypes(v); setPage(1); }}
          style={{ minWidth: 170 }}
          size="small"
          allowClear
          maxTagCount={2}
        >
          {Object.entries(EMAIL_TYPE_CFG).map(([k, v]) => (
            <Option key={k} value={k}>
              <span style={{ color: v.color }}>{v.label}</span>
            </Option>
          ))}
        </Select>

        <RangePicker
          size="small"
          value={dateRange}
          onChange={v => { setDateRange(v || [null, null]); setPage(1); }}
          format="MMM D, YYYY"
          style={{ borderRadius: 6 }}
        />

        {filterActive && (
          <button
            onClick={() => {
              setSearch(""); setStatuses([]); setTypes([]); setDateRange([null, null]); setPage(1);
            }}
            style={{
              padding: "4px 10px", borderRadius: 6, border: "1px solid #fca5a5",
              background: "#fef2f2", color: "#ef4444", fontSize: 12, fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        )}

        <span style={{ marginLeft: "auto", fontSize: 12, color: "#94a3b8" }}>
          {total} email{total !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Table */}
      <div style={{
        background: "#fff", border: "1px solid #e2e8f0",
        borderRadius: 12, overflow: "hidden",
        boxShadow: "0 1px 4px #0001",
      }}>
        <Table
          dataSource={rows}
          columns={columns}
          rowKey={r => r._id || r.messageId || r.trackId}
          loading={{ spinning: loading, indicator: <Spin size="small" /> }}
          size="small"
          scroll={{ x: 1100 }}
          expandable={{
            expandedRowRender: record => <ExpandedRow record={record} />,
            expandIcon: ({ expanded, onExpand, record }) => (
              <button
                onClick={e => onExpand(record, e)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "#94a3b8", padding: "0 4px", fontSize: 11,
                }}
              >
                <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
              </button>
            ),
            rowExpandable: r =>
              (r.opens?.length > 0) ||
              r.status === "replied" ||
              ["bounced","soft_bounced","complained","rejected"].includes(r.status) ||
              !!r.parentMessageId,
          }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: false,
            showTotal: (t, [f, l]) => (
              <span style={{ fontSize: 12, color: "#64748b" }}>
                {f}–{l} of {t}
              </span>
            ),
            onChange: p => { setPage(p); fetchHistory({ page: p }); },
            style: { padding: "12px 16px" },
          }}
          rowClassName={r =>
            r.status === "replied"                        ? "row-replied"      :
            ["bounced","complained","rejected"].includes(r.status) ? "row-bounced" :
            r.status === "soft_bounced"                   ? "row-soft-bounce"  : ""
          }
        />
      </div>

      <style>{`
        .row-replied td { background: #faf5ff !important; }
        .row-bounced td { background: #fff5f5 !important; }
        .row-soft-bounce td { background: #fff8f0 !important; }
        .ant-table-row:hover .row-replied td,
        .ant-table-row:hover .row-bounced td,
        .ant-table-row:hover .row-soft-bounce td { filter: brightness(0.97); }
      `}</style>
    </div>
  );
}
